/**
 * GeoJSON utilities for converting SPARQL WKT geometries to GeoJSON features
 * Used for Mapbox GL JS rendering of railway network coverage areas
 */

import type {
  GeoJSONGeometry,
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  OperationalPoint,
  RailwayLine,
  RailMLFileWithParts,
  FeatureProperties
} from '@/types';

/**
 * Parse WKT (Well-Known Text) string to GeoJSON geometry
 * Supports POINT, LINESTRING, POLYGON in EPSG:4326 (WGS84)
 * @param wkt - WKT string from SPARQL query
 * @returns GeoJSON geometry object or null if parsing fails
 */
export function wktToGeoJSON(wkt: string): GeoJSONGeometry | null {
  if (!wkt || typeof wkt !== 'string') {
    console.warn('Invalid WKT input:', wkt);
    return null;
  }

  // Remove CRS prefix if present (e.g., "<http://www.opengis.net/def/crs/EPSG/0/4326>")
  const cleanWkt = wkt.replace(/<[^>]+>/g, '').trim();

  try {
    // POINT(10.7522 59.9139)
    if (cleanWkt.startsWith('POINT')) {
      const coords = cleanWkt.match(/POINT\s*\(\s*([\d.-]+)\s+([\d.-]+)\s*\)/);
      if (!coords) throw new Error('Invalid POINT format');
      return {
        type: 'Point',
        coordinates: [parseFloat(coords[1]), parseFloat(coords[2])]
      };
    }

    // LINESTRING(10.75 59.91, 10.76 59.92, 10.77 59.93)
    if (cleanWkt.startsWith('LINESTRING')) {
      const match = cleanWkt.match(/LINESTRING\s*\(([^)]+)\)/);
      if (!match) throw new Error('Invalid LINESTRING format');
      const coordPairs = match[1]
        .split(',')
        .map(pair => {
          const [lon, lat] = pair.trim().split(/\s+/);
          return [parseFloat(lon), parseFloat(lat)];
        });
      return {
        type: 'LineString',
        coordinates: coordPairs
      };
    }

    // POLYGON((10.75 59.91, 10.76 59.91, 10.76 59.92, 10.75 59.92, 10.75 59.91))
    if (cleanWkt.startsWith('POLYGON')) {
      const match = cleanWkt.match(/POLYGON\s*\((.+)\)/);
      if (!match) throw new Error('Invalid POLYGON format');
      const rings = match[1]
        .split(/\)\s*,\s*\(/)
        .map(ring => {
          const cleanRing = ring.replace(/[()]/g, '');
          return cleanRing.split(',').map(pair => {
            const [lon, lat] = pair.trim().split(/\s+/);
            return [parseFloat(lon), parseFloat(lat)];
          });
        });
      return {
        type: 'Polygon',
        coordinates: rings
      };
    }

    // MULTIPOLYGON(((10.75 59.91, 10.76 59.91, 10.76 59.92, 10.75 59.92, 10.75 59.91)), (...))
    if (cleanWkt.startsWith('MULTIPOLYGON')) {
      const match = cleanWkt.match(/MULTIPOLYGON\s*\((.+)\)/);
      if (!match) throw new Error('Invalid MULTIPOLYGON format');
      const polygons = match[1]
        .split(/\)\s*\)\s*,\s*\(\s*\(/)
        .map(polygon => {
          const cleanPoly = polygon.replace(/[()]/g, '');
          const rings = cleanPoly.split(/\)\s*,\s*\(/).map(ring => {
            return ring.split(',').map(pair => {
              const [lon, lat] = pair.trim().split(/\s+/);
              return [parseFloat(lon), parseFloat(lat)];
            });
          });
          return rings;
        });
      return {
        type: 'MultiPolygon',
        coordinates: polygons
      };
    }

    console.warn('Unsupported WKT geometry type:', cleanWkt.substring(0, 50));
    return null;
  } catch (error) {
    console.error('Error parsing WKT:', error, 'Input:', cleanWkt);
    return null;
  }
}

/**
 * Create GeoJSON FeatureCollection for map coverage layers
 * @param operationalPoints - Array of operational point entities with WKT geometry
 * @param lines - Array of line entities with WKT geometry
 * @param railmlFiles - Array of railML file entities with hasPart relationships
 * @returns GeoJSON FeatureCollection
 */
export function createCoverageLayer(
  operationalPoints: OperationalPoint[],
  lines: RailwayLine[],
  railmlFiles: RailMLFileWithParts[]
): GeoJSONFeatureCollection {
  const features: GeoJSONFeature[] = [];

  // Operational points as circle features
  if (operationalPoints && Array.isArray(operationalPoints)) {
    operationalPoints.forEach((op: OperationalPoint) => {
      if (!op.wkt) return;

      const geometry = wktToGeoJSON(op.wkt);
      if (!geometry) return;

      // Find associated railML files
      const associatedFiles = railmlFiles
        ? railmlFiles
          .filter(
            (f: RailMLFileWithParts) => f.hasPart && f.hasPart.some((part: string) => part === op.uri)
          )
          .map((f: RailMLFileWithParts) => ({
            uri: f.uri,
            title: f.title,
            filename: f.filename || ''
          }))
        : [];

      features.push({
        type: 'Feature',
        geometry,
        properties: {
          id: op.uri,
          type: 'operationalPoint',
          label: op.label_no || op.label_en || 'Unknown',
          description: `${op.operationalType || 'Operational Point'}: ${op.label_no || op.label_en || 'Unknown'}`,
          operationalType: op.operationalType,
          trafficType: op.trafficType,
          railmlFiles: associatedFiles,
          fileCount: associatedFiles.length
        }
      });
    });
  }

  // Lines as polygon/linestring features
  if (lines && Array.isArray(lines)) {
    lines.forEach((line: RailwayLine) => {
      if (!line.wkt) return;

      const geometry = wktToGeoJSON(line.wkt);
      if (!geometry) return;

      // Find associated railML files
      const associatedFiles = railmlFiles
        ? railmlFiles
          .filter(
            (f: RailMLFileWithParts) => f.hasPart && f.hasPart.some((part: string) => part === line.uri)
          )
          .map((f: RailMLFileWithParts) => ({
            uri: f.uri,
            title: f.title,
            filename: f.filename || ''
          }))
        : [];

      features.push({
        type: 'Feature',
        geometry,
        properties: {
          id: line.uri,
          type: 'line',
          label: line.label_no || line.label_en || 'Unknown',
          description:
            line.description_no || line.description_en || 'Railway Line',
          isLineSection: !!line.parent_line,
          railmlFiles: associatedFiles,
          fileCount: associatedFiles.length
        }
      });
    });
  }

  return {
    type: 'FeatureCollection',
    features
  };
}

/**
 * Get bounding box for a set of features
 * @param featureCollection - GeoJSON FeatureCollection
 * @returns Bounding box [minLon, minLat, maxLon, maxLat]
 */
export function getBounds(featureCollection: GeoJSONFeatureCollection): [number, number, number, number] {
  if (
    !featureCollection ||
    !featureCollection.features ||
    featureCollection.features.length === 0
  ) {
    // Default to Norway bounds if no features
    return [4.5, 58.0, 31.5, 71.5]; // [minLon, minLat, maxLon, maxLat]
  }

  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  featureCollection.features.forEach((feature: GeoJSONFeature) => {
    if (!feature.geometry) return;

    const coords = getAllCoordinates(feature.geometry);
    coords.forEach(([lon, lat]: [number, number]) => {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    });
  });

  return [minLon, minLat, maxLon, maxLat];
}

/**
 * Extract all coordinates from a GeoJSON geometry
 * @param geometry - GeoJSON geometry object
 * @returns Array of [lon, lat] pairs
 */
function getAllCoordinates(geometry: GeoJSONGeometry): Array<[number, number]> {
  if (!geometry || !geometry.type) return [];

  const coords = geometry.coordinates;

  switch (geometry.type) {
    case 'Point':
      return [coords as [number, number]];
    case 'LineString':
    case 'MultiPoint':
      return coords as Array<[number, number]>;
    case 'Polygon':
    case 'MultiLineString':
      return (coords as number[][][]).flat() as Array<[number, number]>;
    case 'MultiPolygon':
      return (coords as number[][][][]).flat(2) as Array<[number, number]>;
    default:
      return [];
  }
}

/**
 * Create a GeoJSON Feature from geometry and properties
 * @param geometry - GeoJSON geometry object
 * @param properties - Feature properties
 * @returns GeoJSON Feature
 */
export function geoJSONToFeature(geometry: GeoJSONGeometry, properties: Partial<FeatureProperties> = {}): GeoJSONFeature {
  const defaultProperties: FeatureProperties = {
    id: '',
    type: 'operationalPoint',
    label: '',
    description: '',
    ...properties
  };
  return {
    type: 'Feature',
    geometry,
    properties: defaultProperties
  };
}
